import { Navigation, Phone, ExternalLink, CalendarCheck, Instagram, Music2 } from "lucide-react";

// ─── CTA button types ───────────────────────────────────────────────────
// Neutral buttons + branded social (Instagram / TikTok) with their own colours.
export const CTA_TYPES = [
  { type: "directions", icon: Navigation, brand: false, label: { it: "Indicazioni", en: "Directions", fr: "Itinéraire", es: "Cómo llegar", de: "Route" }, placeholder: "https://maps.google.com/…" },
  { type: "call", icon: Phone, brand: false, label: { it: "Chiama", en: "Call", fr: "Appeler", es: "Llamar", de: "Anrufen" }, placeholder: "+39 06 …" },
  { type: "website", icon: ExternalLink, brand: false, label: { it: "Sito web", en: "Website", fr: "Site web", es: "Sitio web", de: "Webseite" }, placeholder: "https://…" },
  { type: "book", icon: CalendarCheck, brand: false, label: { it: "Prenota", en: "Book", fr: "Réserver", es: "Reservar", de: "Buchen" }, placeholder: "https://…" },
  { type: "instagram", icon: Instagram, brand: true, label: { it: "Instagram", en: "Instagram", fr: "Instagram", es: "Instagram", de: "Instagram" }, placeholder: "https://instagram.com/…" },
  { type: "tiktok", icon: Music2, brand: true, label: { it: "TikTok", en: "TikTok", fr: "TikTok", es: "TikTok", de: "TikTok" }, placeholder: "https://tiktok.com/@…" },
];
export const CTA_BY_TYPE = Object.fromEntries(CTA_TYPES.map((c) => [c.type, c]));

// Brand styles for social CTAs
export const ctaStyle = (type) => {
  if (type === "instagram") return { background: "linear-gradient(135deg,#F58529 0%,#DD2A7B 45%,#8134AF 75%,#515BD4 100%)", color: "#fff" };
  if (type === "tiktok") return { background: "#000", color: "#fff" };
  return { background: "#ffffff", color: "#18181b" }; // neutral
};

// Resolve a CTA href (call → tel:)
export const ctaHref = (btn) => {
  if (!btn?.value) return "#";
  if (btn.type === "call") return `tel:${btn.value.replace(/\s+/g, "")}`;
  return btn.value;
};

// ─── Category tags (ring colour per category) ───────────────────────────
export const CATEGORY_TAGS = [
  { id: "cibo", color: "#FF6B4A", label: { it: "Cibo", en: "Food", fr: "Cuisine", es: "Comida", de: "Essen" } },
  { id: "natura", color: "#22C55E", label: { it: "Natura", en: "Nature", fr: "Nature", es: "Naturaleza", de: "Natur" } },
  { id: "vita_notturna", color: "#8B5CF6", label: { it: "Vita notturna", en: "Nightlife", fr: "Vie nocturne", es: "Vida nocturna", de: "Nachtleben" } },
  { id: "famiglia", color: "#F59E0B", label: { it: "Famiglia", en: "Family", fr: "Famille", es: "Familia", de: "Familie" } },
  { id: "romantico", color: "#EC4899", label: { it: "Romantico", en: "Romantic", fr: "Romantique", es: "Romántico", de: "Romantisch" } },
  { id: "pioggia", color: "#3B82F6", label: { it: "Giorno di pioggia", en: "Rainy day", fr: "Jour de pluie", es: "Día de lluvia", de: "Regentag" } },
];
export const CAT_BY_ID = Object.fromEntries(CATEGORY_TAGS.map((c) => [c.id, c]));

// ─── Visibility condition options ───────────────────────────────────────
export const DAYS = [
  { id: "mon", label: { it: "Lun", en: "Mon", fr: "Lun", es: "Lun", de: "Mo" } },
  { id: "tue", label: { it: "Mar", en: "Tue", fr: "Mar", es: "Mar", de: "Di" } },
  { id: "wed", label: { it: "Mer", en: "Wed", fr: "Mer", es: "Mié", de: "Mi" } },
  { id: "thu", label: { it: "Gio", en: "Thu", fr: "Jeu", es: "Jue", de: "Do" } },
  { id: "fri", label: { it: "Ven", en: "Fri", fr: "Ven", es: "Vie", de: "Fr" } },
  { id: "sat", label: { it: "Sab", en: "Sat", fr: "Sam", es: "Sáb", de: "Sa" } },
  { id: "sun", label: { it: "Dom", en: "Sun", fr: "Dim", es: "Dom", de: "So" } },
];
export const WEATHER_OPTS = [
  { id: "sunny", label: { it: "Sole", en: "Sunny", fr: "Soleil", es: "Sol", de: "Sonne" } },
  { id: "cloudy", label: { it: "Nuvoloso", en: "Cloudy", fr: "Nuageux", es: "Nublado", de: "Bewölkt" } },
  { id: "rain", label: { it: "Pioggia", en: "Rain", fr: "Pluie", es: "Lluvia", de: "Regen" } },
];
export const SEASONS = [
  { id: "spring", label: { it: "Primavera", en: "Spring", fr: "Printemps", es: "Primavera", de: "Frühling" } },
  { id: "summer", label: { it: "Estate", en: "Summer", fr: "Été", es: "Verano", de: "Sommer" } },
  { id: "autumn", label: { it: "Autunno", en: "Autumn", fr: "Automne", es: "Otoño", de: "Herbst" } },
  { id: "winter", label: { it: "Inverno", en: "Winter", fr: "Hiver", es: "Invierno", de: "Winter" } },
];
export const TIMES = [
  { id: "morning", label: { it: "Mattina", en: "Morning", fr: "Matin", es: "Mañana", de: "Morgen" } },
  { id: "afternoon", label: { it: "Pomeriggio", en: "Afternoon", fr: "Après-midi", es: "Tarde", de: "Nachmittag" } },
  { id: "evening", label: { it: "Sera", en: "Evening", fr: "Soir", es: "Noche", de: "Abend" } },
  { id: "night", label: { it: "Notte", en: "Night", fr: "Nuit", es: "Noche", de: "Nacht" } },
];

// Short dynamic label under each preview circle, derived from the story's
// conditions/category. Localised.
const L = (obj, lang) => obj[lang] || obj.it;
export const circleLabel = (story, lang) => {
  const c = story.conditions || {};
  const tod = c.time_of_day || [];
  const wx = c.weather || [];
  const days = c.days_of_week || [];
  if (tod.includes("evening") || tod.includes("night")) return L({ it: "Stasera", en: "Tonight", fr: "Ce soir", es: "Esta noche", de: "Heute Abend" }, lang);
  if (tod.includes("morning")) return L({ it: "Stamattina", en: "This morning", fr: "Ce matin", es: "Esta mañana", de: "Heute früh" }, lang);
  if (wx.includes("rain")) return L({ it: "Se piove", en: "If it rains", fr: "S'il pleut", es: "Si llueve", de: "Bei Regen" }, lang);
  if (days.includes("sat") || days.includes("sun")) return L({ it: "Weekend", en: "Weekend", fr: "Week-end", es: "Finde", de: "Wochenende" }, lang);
  const cat = (story.category_tags || [])[0];
  if (cat && CAT_BY_ID[cat]) return L(CAT_BY_ID[cat].label, lang);
  return L({ it: "Da provare", en: "To try", fr: "À essayer", es: "Para probar", de: "Zum Probieren" }, lang);
};

// Ring style (Instagram-like): host's own stories = red/pink gradient,
// community stories = blue/cyan gradient, already-seen = neutral grey.
export const ringStyle = (story, seen) => {
  if (seen) return "#d4d4d8";
  if (story.source === "community") return "linear-gradient(135deg,#3B82F6 0%,#22D3EE 100%)";
  return "linear-gradient(135deg,#FF6B4A 0%,#FF2D74 100%)";
};

// ─── Grouping (Instagram-style) ─────────────────────────────────────────
// Each dynamic label ("Stasera", "Se piove", …) becomes ONE bubble that
// holds all its stories, viewed back-to-back. Order of first appearance is
// preserved so the backend context ranking still drives which bucket leads.
export const groupStories = (stories, lang) => {
  const map = new Map();
  (stories || []).forEach((s) => {
    const label = circleLabel(s, lang);
    if (!map.has(label)) map.set(label, []);
    map.get(label).push(s);
  });
  return [...map.entries()].map(([label, items]) => ({
    label,
    stories: items,
    cover: items[0],
    hasOwn: items.some((x) => x.source !== "community"),
    hasCommunity: items.some((x) => x.source === "community"),
  }));
};

// Ring for a grouped bubble: red if it contains at least one of the host's
// own stories, otherwise blue (community-only). Grey when all seen.
export const groupRingStyle = (group, seen) => {
  if (seen) return "#d4d4d8";
  if (group.hasOwn) return "linear-gradient(135deg,#FF6B4A 0%,#FF2D74 100%)";
  return "linear-gradient(135deg,#3B82F6 0%,#22D3EE 100%)";
};

// Ring colour for a story circle (first matching category, else brand default)
export const circleColor = (story) => {
  const cat = (story.category_tags || [])[0];
  return (cat && CAT_BY_ID[cat]?.color) || "#FF6B4A";
};

export const STORIES_TITLE = { it: "Scopri la Zona", en: "Discover the Area", fr: "Découvrir le Coin", es: "Descubre la Zona", de: "Entdecke die Gegend" };
